/**
 * Módulo de Preguntas y Respuestas
 * Público: Todos pueden ver preguntas y respuestas
 * Proponentes: Pueden crear preguntas
 * Administradores: Pueden responder preguntas
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MessageCircle, Send, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import apiConfig from '../../config/api.config';

const Container = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
`;

const FormCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const FormTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : theme.colors.white};
  color: ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.white : theme.colors.textPrimary};
  border: 2px solid ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreguntasList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const PreguntaCard = styled.div<{ respondida: boolean }>`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border-left: 4px solid ${({ theme, respondida }) => 
    respondida ? theme.colors.success : theme.colors.warning};
`;

const PreguntaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PreguntaInfo = styled.div`
  flex: 1;
`;

const Proveedor = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Fecha = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatusBadge = styled.span<{ estado: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: ${({ estado }) => 
    estado === 'RESPONDIDA' ? '#10b98120' : '#f59e0b20'};
  color: ${({ estado }) => 
    estado === 'RESPONDIDA' ? '#10b981' : '#f59e0b'};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const PreguntaTexto = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const RespuestaSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
`;

const RespuestaLabel = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.success};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const RespuestaTexto = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.6;
  padding: ${({ theme }) => theme.spacing.md};
  background: #10b98110;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

interface Pregunta {
  id: number;
  proveedor_username: string;
  proveedor_nombre: string;
  pregunta: string;
  respuesta: string | null;
  respondida_por: string | null;
  estado: 'PENDIENTE' | 'RESPONDIDA';
  fecha_pregunta: string;
  fecha_respuesta: string | null;
}

const PreguntasModule: React.FC = () => {
  const { proveedor, token } = useAuth();
  const isAdmin = proveedor?.username === 'ADMINISTRADOR';
  const [preguntas, setPreguntas] = useState<Pregunta[]>([]);
  const [loading, setLoading] = useState(true);
  const [nuevaPregunta, setNuevaPregunta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [respuestaId, setRespuestaId] = useState<number | null>(null);
  const [respuestaTexto, setRespuestaTexto] = useState('');

  const fetchPreguntas = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiConfig.endpoints.preguntas);
      const data = await response.json();
      
      if (response.ok) {
        setPreguntas(data.preguntas);
      }
    } catch (error) {
      console.error('Error al cargar preguntas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreguntas();
  }, []);

  const handleCrearPregunta = async () => {
    if (!nuevaPregunta.trim()) {
      alert('Por favor escribe tu pregunta');
      return;
    }

    if (!token) {
      alert('No hay token de autenticación. Por favor inicia sesión nuevamente.');
      return;
    }

    console.log('Token enviado:', token ? 'Presente' : 'No presente');
    console.log('Proveedor:', proveedor);

    setEnviando(true);
    try {
      const response = await fetch(apiConfig.endpoints.preguntasCrear, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ pregunta: nuevaPregunta }),
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        alert('Pregunta enviada exitosamente');
        setNuevaPregunta('');
        fetchPreguntas();
      } else {
        const data = await response.json().catch(() => ({ error: 'Error desconocido' }));
        console.error('Error del servidor:', data);
        alert(`Error: ${data.error || data.msg || 'Error al enviar pregunta'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al enviar pregunta');
    } finally {
      setEnviando(false);
    }
  };

  const handleResponder = async (preguntaId: number) => {
    if (!respuestaTexto.trim()) {
      alert('Por favor escribe una respuesta');
      return;
    }

    setEnviando(true);
    try {
      const response = await fetch(apiConfig.endpoints.preguntasResponder, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          pregunta_id: preguntaId,
          respuesta: respuestaTexto,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert('Respuesta enviada exitosamente');
        setRespuestaId(null);
        setRespuestaTexto('');
        fetchPreguntas();
      } else {
        alert(data.error || 'Error al enviar respuesta');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al enviar respuesta');
    } finally {
      setEnviando(false);
    }
  };

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Container>
      <Header>
        <Title>
          <MessageCircle size={32} />
          Preguntas y Respuestas
        </Title>
        <Subtitle>
          Espacio público para preguntas sobre la licitación - Total: {preguntas.length}
        </Subtitle>
      </Header>

      {!isAdmin && (
        <FormCard>
          <FormTitle>Hacer una Pregunta</FormTitle>
          <TextArea
            placeholder="Escribe tu pregunta aquí..."
            value={nuevaPregunta}
            onChange={(e) => setNuevaPregunta(e.target.value)}
            disabled={enviando}
          />
          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleCrearPregunta}
              disabled={enviando || !nuevaPregunta.trim()}
            >
              <Send size={18} />
              {enviando ? 'Enviando...' : 'Enviar Pregunta'}
            </Button>
            <Button onClick={fetchPreguntas}>
              <RefreshCw size={18} />
              Actualizar
            </Button>
          </ButtonGroup>
        </FormCard>
      )}

      {isAdmin && (
        <ButtonGroup style={{ justifyContent: 'flex-start', marginBottom: '2rem' }}>
          <Button onClick={fetchPreguntas}>
            <RefreshCw size={18} />
            Actualizar
          </Button>
        </ButtonGroup>
      )}

      {loading ? (
        <EmptyState>Cargando preguntas...</EmptyState>
      ) : preguntas.length === 0 ? (
        <EmptyState>No hay preguntas todavía. ¡Sé el primero en preguntar!</EmptyState>
      ) : (
        <PreguntasList>
          {preguntas.map((pregunta) => (
            <PreguntaCard key={pregunta.id} respondida={pregunta.estado === 'RESPONDIDA'}>
              <PreguntaHeader>
                <PreguntaInfo>
                  <Proveedor>{pregunta.proveedor_nombre}</Proveedor>
                  <Fecha>{formatFecha(pregunta.fecha_pregunta)}</Fecha>
                </PreguntaInfo>
                <StatusBadge estado={pregunta.estado}>
                  {pregunta.estado === 'RESPONDIDA' ? (
                    <><CheckCircle size={14} /> Respondida</>
                  ) : (
                    <><Clock size={14} /> Pendiente</>
                  )}
                </StatusBadge>
              </PreguntaHeader>

              <PreguntaTexto>{pregunta.pregunta}</PreguntaTexto>

              {pregunta.respuesta ? (
                <RespuestaSection>
                  <RespuestaLabel>
                    <CheckCircle size={18} />
                    Respuesta del Administrador
                  </RespuestaLabel>
                  <RespuestaTexto>{pregunta.respuesta}</RespuestaTexto>
                  <Fecha style={{ marginTop: '0.5rem' }}>
                    Respondido el {formatFecha(pregunta.fecha_respuesta!)}
                  </Fecha>
                </RespuestaSection>
              ) : isAdmin ? (
                <RespuestaSection>
                  {respuestaId === pregunta.id ? (
                    <>
                      <TextArea
                        placeholder="Escribe tu respuesta..."
                        value={respuestaTexto}
                        onChange={(e) => setRespuestaTexto(e.target.value)}
                        disabled={enviando}
                        style={{ minHeight: '80px' }}
                      />
                      <ButtonGroup>
                        <Button
                          variant="primary"
                          onClick={() => handleResponder(pregunta.id)}
                          disabled={enviando || !respuestaTexto.trim()}
                        >
                          <Send size={18} />
                          {enviando ? 'Enviando...' : 'Enviar Respuesta'}
                        </Button>
                        <Button onClick={() => {
                          setRespuestaId(null);
                          setRespuestaTexto('');
                        }}>
                          Cancelar
                        </Button>
                      </ButtonGroup>
                    </>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() => setRespuestaId(pregunta.id)}
                    >
                      <MessageCircle size={18} />
                      Responder
                    </Button>
                  )}
                </RespuestaSection>
              ) : null}
            </PreguntaCard>
          ))}
        </PreguntasList>
      )}
    </Container>
  );
};

export default PreguntasModule;

